<template>
  <div class="campaign-modal">
    <transition name="fade" @after-enter="afterEnterModal">
      <modal
          v-if="modalConfig"
          :contentAsHtml="modalConfig.contentAsHtml"
          :isWarningModal="modalConfig.isWarningModal"
          showClose
          warningProceedText="yes"
          :wrapperClasses="['confirm-send', { ['filters-warning']: showFiltersWarning }]"
          @action="processModalAction(modalConfig.action)"
          @close="closeWarning"
          @proceed="processModalAction(modalConfig.action)">
        <h3 v-if="modalConfig.title"
            slot="header"
            class="home-screen-title">
          {{modalConfig.title}}
        </h3>

        <div v-if="modalConfig.content || modalConfig.creditHighlight" slot="body" class="mms-campaign-warning">
          <p>{{modalConfig.content}}</p>
          <h3 v-html="modalConfig.creditHighlight">{{modalConfig.creditHighlight}}</h3>
        </div>

        <div
            v-if="!modalConfig.isWarningModal"
            slot="action"
            class="send-mms-campaign button larger modal-click-action"
            @click="processModalAction(modalConfig.action)">
          {{modalConfig.actionButton}}
        </div>
      </modal>
    </transition>
  </div>
</template>

<script>
  import { mapGetters, mapState } from 'vuex'
  import Modal from '../../components/shared/Modal'
  import ModalMixin from '../../mixins/ModalMixin'

  export default {
    props: {
      backupAdOverCreditLimit: Boolean,
      maxAdCampaignCredits: [Number, String],
      parentAdOverCreditLimit: Boolean,
      showBackup: Boolean,
      showFiltersWarning: Boolean,
      showWarning: Boolean,
      smsSend: Boolean,
    },

    name: 'CampaignWarningModal',

    components: {
      Modal,
    },

    mixins: [ModalMixin],

    data() {
      return {
        deliverabilityTitle: 'campaign deliverability',
      }
    },

    methods: {
      closeWarning() {
        const buttonAction = this.modalConfig?.action,
          shouldShowBackup = buttonAction === 'cancelBackup'
        this.modalConfig = null
        this.$emit('closeWarning')
        shouldShowBackup && this.$emit('setShowBackupStatus', true)
      },

      onShowBackupChange() {
        const shouldShowWarning = !!this.backupId && !this.showBackup && this.smsSend
        shouldShowWarning && this.setCancelBackupModal()
      },

      onShowWarningChange() {
        const showAdCreditWarning = this.showBrands && (this.parentAdOverCreditLimit || this.backupAdOverCreditLimit)
        if (showAdCreditWarning) {
          this.setAdsModal(this.parentAdOverCreditLimit, this.backupAdOverCreditLimit)
        } else {
          this.setHasEmojiMediaModal()
        }
      },

      processModalAction(action) {
        this.$emit(action)
        this.modalConfig = null
      },

      setAdsModal(parentOverLimit, backupOverLimit) {
        if (this.hasAd || this.backupHasAd) {
          const title = 'over credit limit',
            campaignType = parentOverLimit && backupOverLimit ? 'message & backup ' : backupOverLimit ? 'backup ' : '',
            contentStart = `sponsored campaigns can be up to ${this.maxAdCampaignCredits} credits`,
            content = `${contentStart}, please adjust the size of your ${campaignType}message to send.`,
            backupCreditHighlight = `<br /> backup credits: ${this.backupTotalCredits}`
          let creditHighlight = `credits: ${this.totalCredits}`
          creditHighlight = this.hasActiveBackup ? `${creditHighlight} ${backupCreditHighlight}` : creditHighlight

          this.modalConfig = {
            action: 'closeWarning',
            actionButton: `continue`,
            content,
            creditHighlight,
            title
          }
        }
      },

      setCancelBackupModal() {
        this.modalConfig = {
          action: 'cancelBackup',
          content: 'are you sure you want to cancel optimal deliverability?',
          isWarningModal: true,
          title: this.deliverabilityTitle
        }
      },
      
      setFiltersWarning() {
        const title = "<h3 class='filter-title'>you have chosen to send this message to <strong>all customers</strong> in this account</h3>",
            toContinue = "<p class='main'>to continue and send this message, select 'send to all customers' below</p>",
            toReturn = "<p class='small'>if you intended to segment your customer base and send this message to only <strong>a portion</strong> of your customers, please close this box & add a filter to your message</p>"
        this.modalConfig = {
          action: 'sendCampaign',
          actionButton: 'send to all customers',
          contentAsHtml: `${title}${toContinue}${toReturn}`
        }
      },

      setHasEmojiMediaModal() {
        const activeBackup = this.hasActiveBackup,
          mmsCount = this.mediaCredits || this.backupMediaCredits,
          mmsType = this.mmsType || this.backupMmsType,
          hasEmoji = this.hasEmoji || this.backupHasEmoji,
          hasEmojiMms = hasEmoji && !!mmsCount,
          emojiMessage = 'an emoji / special character is included in a message, you only have 67 characters per credit, not 160',
          deliverabilityUsed = activeBackup ? `to optimize ${this.deliverabilityTitle}` : null,
          emojiUsed = hasEmoji ? 'emoji / special character' : null,
          mmsUsed = mmsCount ? `${mmsCount} ${mmsType}` : null,
          emojiMmsUsed = hasEmojiMms ? `${mmsUsed} & ${emojiUsed}` : null,
          specialItemsUsed = deliverabilityUsed || emojiMmsUsed || mmsUsed || emojiUsed,
          choseOrUsed = activeBackup ? 'chose' : 'used',
          twoCreditsMessage = `${mmsType} is included in a message, a minimum of 2 credits are charged`,
          message = hasEmojiMms ? `${twoCreditsMessage} and when ${emojiMessage}` : mmsCount ? twoCreditsMessage : hasEmoji ? emojiMessage : '',
          titleUsed = hasEmojiMms ? `${mmsType} & emoji` : mmsCount ? mmsType : 'emoji'

        let messageStart = `you ${choseOrUsed} ${specialItemsUsed} in this message - that's awesome!`
        messageStart = activeBackup && !hasEmoji && !mmsCount ? messageStart : `${messageStart}  heads up though - once`

        this.modalConfig = {
          action: 'confirmedModal',
          actionButton: activeBackup ? 'send campaign' : `send ${this.totalCredits} credit campaign`,
          content: `${messageStart} ${message}`,
          creditHighlight: activeBackup ? null : `this is a ${this.totalCredits} credit message`,
          title: activeBackup ? this.deliverabilityTitle : `${titleUsed} in this message`
        }
      },
    },

    computed: {
      hasActiveBackup: function () {
        return !!this.backupId && this.showBackup
      },

      ...mapGetters({
        backupHasAd: 'backup/hasAd',
        backupTotalCredits: 'backup/totalCredits',
        hasAd: 'message/hasAd',
        backupMediaCredits: 'backup/mediaCredits',
        backupMmsType: 'backup/mmsType',
        mediaCredits: 'message/mediaCredits',
        mmsType: 'message/mmsType',
        showBrands: 'showBrands',
        totalCredits: 'message/totalCredits'
      }),

      ...mapState({
        credits: state => state.message.credits,
        backupHasEmoji: state => state.backup.hasEmoji,
        backupId: state => state.backup.messageId,
        hasEmoji: state => state.message.hasEmoji
      }),
    },

    watch: {
      showBackup: function () {
        this.onShowBackupChange()
      },
      
      showFiltersWarning: function () {
        if (this.showFiltersWarning) { this.setFiltersWarning() }
      },

      showWarning: function () {
        this.showWarning && this.onShowWarningChange()
      }
    }
  }
</script>
